import React, {useContext, useEffect, useMemo, useState} from "react";
import {Button, Grid, Paper, Stack, Typography, useTheme} from "@mui/material";
import {CheckCircleOutlineRounded} from "@mui/icons-material";
import {useNavigate, useParams} from "react-router-dom";
import {useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "@tanstack/react-query";
import {FormTextField} from "../common/form-fields-wrappers";
import {IPaymentReconciliationForm, IPreAuthDataForm} from "../models/models";
import QrCodeDialog from "../create-ra/qr-code-dialog/QrCodeDialog";
import CustomLoader from "../../../util/component/CustomLoader";
import {GlobalContext} from "../../../util/state/GlobalContextProvider";
import SectionHeader from "../../../util/component/SectionHeader";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {formatCurrency, getLocaleByLang} from "../utils";
import ContentBody from "../common/content-body/ContentBody";
import {LOCALES, POLLING_INTERVAL_MS} from "../config";
import {PAYMENT_RECONCILIATION_FORM_INITIAL_VALUE} from "./config";
import {
    raDispositivePaymentReconciliation, raPaymentReconciliationVerifyStatus,
    raPaymentToBeReconciled,
    raPreAuthPaymentReconciliation,
    raPreAuthPaymentReconciliationVerify
} from "./services/http.services";
import EmptyState from "../../../util/component/EmptyState";

const PaymentReconciliation = () => {
    const theme = useTheme();
    const {t} = useTranslation(['network', 'translation']);
    const appLanguage = useTranslation();
    const { dispatchGlobalData } = useContext(GlobalContext);
    const param = useParams();
    const navigate = useNavigate();

    const [raPaymentData, setRaPaymentData] = useState<any>();
    const [raPaymentStatus, setRaPaymentStatus] = useState<any>();
    const [verifyPaymentRecStatus, setVerifyPaymentRecStatus] = useState<any>();
    const [openPaymentReconciliation, setOpenPaymentReconciliation] = useState<boolean>(false);
    const [paymentReconciliationTitle, setPaymentReconciliationTitle] = useState<string>('');
    const [verifyRefetchFlag, setVerifyRefetchFlag] = useState<boolean>(false);
    const [qrCode, setQrCode] = useState<string | null>('');
    const [actionType, setActionType] = useState<'firstPreAuth' | 'secondPreAuth' | 'dispositive' | null>(null);

    const locale = useMemo((): 'it-IT' | 'en-IN' | 'fr-FR' => {
        return getLocaleByLang(appLanguage.i18n.language);
    }, [appLanguage.i18n.language]);

    const oldOpIdName = useMemo<any>(() => {
        return {
            firstPreAuth: 'firstPreauthOperationId',
            secondPreAuth: 'secondPreauthOperationId',
            dispositive: 'dispositiveOperationId'
        }
    }, []);

    const preAuthDataForm = useForm<IPaymentReconciliationForm>({
        defaultValues: PAYMENT_RECONCILIATION_FORM_INITIAL_VALUE as IPaymentReconciliationForm
    });

    /**
     * Init dialog info
     */
    const handlePaymentReconciliation = async (e: React.BaseSyntheticEvent, type: 'firstPreAuth' | 'secondPreAuth' | 'dispositive') => {
        await setActionType(type);
        let payload: any;
        switch(type) {
            case "firstPreAuth":
            case "secondPreAuth":
                const card = type === 'firstPreAuth' ? "firstPreauthAmount" : "secondPreauthAmount";
                payload = {
                    rentalId: param.id,
                    amount: {
                        currency: "EUR",
                        value: raPaymentData[card]
                    },
                    paymentChannel: "POS",
                    tokenizeCard: true,
                    type: type === "firstPreAuth" ? "FIRST" : "SECOND"
                };
                raPreAuthPaymentReconciliationMutation.mutate(payload);
                break;
            case "dispositive":
                payload = {
                    rentalId: param.id,
                    amount: {
                        currency: "EUR",
                        value: raPaymentData.dispositiveAmount
                    },
                    paymentChannel: "POS"
                }
                raDispositivePaymentReconciliationMutation.mutate(payload);
                break;
        }
    }

    const handleGoBack = () => {
        navigate(`/network/ra-detail/${param.id}`);
    }

    /**
     * View Error Message
     * @param type
     * @param message
     */
    const viewErrorMessage = (message: string, type: 0 | 1 | 2 | 3 | 4 = snackbarTypes.error) => {
        dispatchGlobalData({
            type: 'SET_SNACKBAR',
            payload: {
                snackbar: {
                    message: t(message),
                    type: type,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    /* WREST */
    /** Get Payment Payment Reconciliation */
    const raPaymentToBeReconciledMutation = useMutation(
        raPaymentToBeReconciled,
        {
            onSuccess: async (response: any) => {
                preAuthDataForm.setValue('firstPreauthAmount', formatCurrency(locale as LOCALES, response.firstPreauthAmount));
                preAuthDataForm.setValue('secondPreauthAmount', formatCurrency(locale as LOCALES, response.secondPreauthAmount));
                preAuthDataForm.setValue('dispositiveAmount', formatCurrency(locale as LOCALES, response.dispositiveAmount));

                setRaPaymentData(response);
            },
            onError: (error) => {
                viewErrorMessage('translation:error.tryLater');
            }
        });

    /**
     * Get info for PreAuth Payment Reconciliation
     */
    const raPreAuthPaymentReconciliationMutation = useMutation(
        raPreAuthPaymentReconciliation,
        {
            onSuccess: async (response) => onSuccessPreAuthReconciliation(response),
            onError: (error) => {
                viewErrorMessage('translation:error.tryLater');
            }
        });

    /**
     * Get info for Dispositive Payment Reconciliation
     */
    const raDispositivePaymentReconciliationMutation = useMutation(
        raDispositivePaymentReconciliation,
        {
            onSuccess: async (response) => onSuccessPreAuthReconciliation(response),
            onError: (error) => {
                viewErrorMessage('translation:error.tryLater');
            }
        });

    /**
     * Verify operation status polling
     */
    const actionVerifyQuery = useQuery({
        // Refetch the data every interval
        refetchInterval: POLLING_INTERVAL_MS,
        enabled: verifyRefetchFlag,
        queryKey: ['verify', {
            operationId: verifyPaymentRecStatus?.operationId,
            oldOperationId: raPaymentData?.[oldOpIdName[actionType ?? 'noop']]
        }],
        queryFn: async () => raPreAuthPaymentReconciliationVerify(
            {
                operationId: verifyPaymentRecStatus?.operationId,
                oldOperationId: raPaymentData?.[oldOpIdName[actionType ?? 'noop']]
            }),
        onSuccess: async (response: any) => {
            if (response.status === 'OK' || response.status === 'CANCELLED') {
                setVerifyRefetchFlag(false);
                setOpenPaymentReconciliation(false);
                setVerifyPaymentRecStatus(null);
                setPaymentReconciliationTitle('');
                setQrCode(null);
                setActionType(null);

                raPaymentReconciliationVerifyStatusMutation.mutate({
                    idRA: encodeURIComponent(param.id as string)
                } as any);
            }
        },
        onError: (error: any) => {
            viewErrorMessage('translation:error.tryLater');

            setVerifyRefetchFlag(false);
            setOpenPaymentReconciliation(false);
            setVerifyPaymentRecStatus(null);
            setPaymentReconciliationTitle('');
            setQrCode(null);
            setActionType(null);
        }
    })

    /**
     * Verify payment reconciliation status
     */
    const raPaymentReconciliationVerifyStatusMutation = useMutation(
        raPaymentReconciliationVerifyStatus,
        {
            onSuccess: async (response) => {
                setRaPaymentStatus(response);
            },
            onError: (error) => {
                viewErrorMessage('translation:error.tryLater');
            }
        });

    const onSuccessPreAuthReconciliation = (data: any) => {
        // set qr code open
        switch (actionType) {
            case "firstPreAuth":
                setPaymentReconciliationTitle(t("paymentReconciliation.paymentReconciliationDialog.title.firstPreAuth"));
                break;
            case "secondPreAuth":
                setPaymentReconciliationTitle(t("paymentReconciliation.paymentReconciliationDialog.title.secondPreAuth"));
                break;
            case "dispositive":
                setPaymentReconciliationTitle(t("paymentReconciliation.paymentReconciliationDialog.title.payment"));
                break;
        }

        setVerifyRefetchFlag(true);
        setOpenPaymentReconciliation(true);
        setVerifyPaymentRecStatus(data);
        setQrCode(data.qrCode);
    }


    useEffect(() => {
        if (!!param.id) {
            raPaymentToBeReconciledMutation.mutate({idRA: param.id} as any);
            raPaymentReconciliationVerifyStatusMutation.mutate({idRA: encodeURIComponent(param.id)} as any);
        }
    }, [param.id]);

    return (
        <>
            <SectionHeader title={t('paymentReconciliation.header.title')}
                           subtitle={
                               <Stack direction="row" alignItems="center" columnGap={3}>
                                   <Stack direction="row" alignItems="end" columnGap={1}>
                                       <Typography variant="bodyLarge">
                                           {t('paymentReconciliation.header.subtitle.idRentalAgreement')}
                                       </Typography>
                                       <Typography variant="bodyLarge">{param.id}</Typography>
                                   </Stack>
                               </Stack>
                           }
            />

            <ContentBody>
                <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                    <Grid container columnSpacing={2}>
                        {
                            (!!raPaymentData?.firstPreauthAmount || !!raPaymentData?.secondPreauthAmount) &&
                            <Grid item xs={12}>
                                <Typography variant={"h6"}>
                                    {t("paymentReconciliation.preAuthInfo.title")}
                                </Typography>
                            </Grid>
                        }

                        {
                            !!raPaymentData?.firstPreauthAmount &&
                            <>
                                <Grid item xs={12} sx={{mt: theme.spacing(1)}}>
                                    <Typography variant={"bodyLarge"}>
                                        {t("paymentReconciliation.preAuthInfo.subtitle.creditCard.first")}
                                    </Typography>
                                </Grid>

                                <Grid item xs={6} sm={4} md={3} sx={{display: "flex", px: theme.spacing(1)}}>
                                    <FormTextField<IPreAuthDataForm>
                                        name='firstPreauthAmount'
                                        control={preAuthDataForm.control}
                                        label={t("paymentReconciliation.form.label.firstPreAuthAmount")}
                                        fullWidth
                                        disabled
                                    />

                                </Grid>

                                <Grid item xs={6} sm={4} sx={{
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    px: theme.spacing(2),
                                    pb: theme.spacing(0.5),
                                    pt: theme.spacing(1)
                                }}>
                                    {
                                        !raPaymentStatus?.firstPreAuthOk &&
                                        <Button variant={'contained'} color={"primary"}
                                                onClick={(e: React.BaseSyntheticEvent) => handlePaymentReconciliation(e, 'firstPreAuth')}>
                                            {t("paymentReconciliation.buttons.reconciliatePayment")}
                                        </Button>
                                    }

                                    {
                                        raPaymentStatus?.firstPreAuthOk &&
                                        <CheckCircleOutlineRounded
                                            sx={{color: theme.palette.success.main, mx: theme.spacing(2)}}/>
                                    }

                                </Grid>
                            </>
                        }

                        {
                            !!raPaymentData?.secondPreauthAmount &&
                            <>
                                <Grid item xs={12} sx={{mt: theme.spacing(2)}}>
                                    <Typography variant={"bodyLarge"}>
                                        {t("paymentReconciliation.preAuthInfo.subtitle.creditCard.second")}
                                    </Typography>
                                </Grid>

                                <Grid item xs={6} sm={4} md={3} sx={{display: "flex", px: theme.spacing(1)}}>
                                    <FormTextField<IPreAuthDataForm>
                                        name='secondPreauthAmount'
                                        control={preAuthDataForm.control}
                                        label={t("paymentReconciliation.form.label.secondPreAuthAmount")}
                                        fullWidth
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={6} sm={4} sx={{
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    px: theme.spacing(2),
                                    pb: theme.spacing(0.5),
                                    pt: theme.spacing(1)
                                }}>

                                    {
                                        !raPaymentStatus?.secondPreAuthOk &&
                                        <Button variant={'contained'} color={"primary"}
                                                disabled={!raPaymentStatus?.firstPreAuthOk}
                                                onClick={(e: React.BaseSyntheticEvent) => handlePaymentReconciliation(e, 'secondPreAuth')}>
                                            {t("paymentReconciliation.buttons.reconciliatePayment")}
                                        </Button>
                                    }

                                    {
                                        raPaymentStatus?.secondPreAuthOk &&
                                        <CheckCircleOutlineRounded
                                            sx={{color: theme.palette.success.main, mx: theme.spacing(2)}}/>
                                    }
                                </Grid>
                            </>
                        }

                        {
                            !!raPaymentData?.dispositiveAmount &&
                            <>
                                <Grid item xs={12} sx={{mt: theme.spacing(3)}}>
                                    <Typography variant={"h6"}>
                                        {t('paymentReconciliation.paymentInfo.title')}
                                    </Typography>
                                </Grid>

                                <Grid item xs={6} sm={4} md={3} sx={{display: "flex", px: theme.spacing(1)}}>
                                    <FormTextField<IPreAuthDataForm>
                                        name='dispositiveAmount'
                                        control={preAuthDataForm.control}
                                        label={t("paymentReconciliation.form.label.totalAmount")}
                                        fullWidth
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={6} sm={4} sx={{
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    px: theme.spacing(2),
                                    pb: theme.spacing(0.5),
                                    pt: theme.spacing(1)
                                }}>
                                    {
                                        !raPaymentStatus?.dispositiveOk &&
                                        <Button variant={'contained'} color={"primary"}
                                                disabled={!raPaymentStatus?.firstPreAuthOk || !raPaymentStatus?.secondPreAuthOk}
                                                onClick={(e: React.BaseSyntheticEvent) => handlePaymentReconciliation(e, 'dispositive')}>
                                            {t("paymentReconciliation.buttons.reconciliatePayment")}
                                        </Button>
                                    }

                                    {
                                        raPaymentStatus?.dispositiveOk &&
                                        <CheckCircleOutlineRounded
                                            sx={{color: theme.palette.success.main, mx: theme.spacing(2)}}/>
                                    }
                                </Grid>
                            </>
                        }

                        {
                            !!raPaymentData &&
                            !raPaymentData?.firstPreauthAmount &&
                            !raPaymentData?.secondPreauthAmount &&
                            !raPaymentData?.dispositiveAmount &&
                            <Grid item xs={12}>
                                <EmptyState title={''}
                                            content={t('paymentReconciliation.header.noItems')}/>
                            </Grid>
                        }

                        <Grid item xs={12} sx={{mt: theme.spacing(2)}}>
                            <Button variant={'outlined'} color={"primary"}
                                    onClick={handleGoBack}>
                                {t("common.button.backStep")}
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            </ContentBody>

            <CustomLoader loading={raPaymentToBeReconciledMutation.isLoading ||
                raPaymentReconciliationVerifyStatusMutation.isLoading ||
                raDispositivePaymentReconciliationMutation.isLoading ||
                raPreAuthPaymentReconciliationMutation.isLoading
            }/>

            {
                openPaymentReconciliation &&
                <QrCodeDialog
                    dialogTitle={paymentReconciliationTitle}
                    spinner={verifyRefetchFlag}
                    open={verifyRefetchFlag}
                    backdropClick={false}
                    viewQrCodeString={true}
                    qrCode={qrCode}
                />
            }
        </>
    )
}

export default PaymentReconciliation;